import { Fragment } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BRAND } from '@/lib/brand';
import { formatShortDay, formatTooltipDay } from '@/features/marketing/range';
import { LegendDot } from './RankedList';

export interface TrendSeries {
  /** Field on each data point. */
  key: string;
  label: string;
  color: string;
  kind?: 'area' | 'line' | 'bar';
  /** Renders on the right-hand axis — for pairing a rate against a count. */
  axis?: 'left' | 'right';
  /** Tooltip formatter; defaults to a locale integer. */
  format?: (value: number) => string;
  dashed?: boolean;
  /** Stacks bars that share an id (Shopify + Amazon revenue on one column). */
  stackId?: string;
}

/**
 * Generic over the point type so callers can pass their own typed series
 * (`SearchDailyPoint[]`, `SocialDailyPoint[]`, …). A plain
 * `Record<string, unknown>[]` would reject every one of them: TypeScript
 * won't assign an interface without an index signature to a Record.
 */
interface TrendChartProps<T> {
  data: T[];
  series: TrendSeries[];
  height?: number;
  /** Field holding the ISO date. */
  xKey?: string;
  /** Tick formatter for the right-hand axis, when one is in use. */
  formatRightAxis?: (value: number) => string;
  formatLeftAxis?: (value: number) => string;
  /** Highlight a specific x value (the day the operator clicked). */
  highlightedX?: string | null;
  /** Fired with the x-value of the clicked point. */
  onPointClick?: (xValue: string) => void;
}

/**
 * Shared time-series chart for every tab of the Marketing Dashboard.
 *
 * Built on Recharts' `ComposedChart` so a single component covers all the
 * combinations the tabs need — stacked bars for spend, an area for traffic, a
 * dashed line for a comparison rate — without each tab hand-rolling its own
 * axis, grid, and tooltip styling.
 *
 * Series render in declaration order, and areas are emitted before lines so a
 * filled series never paints over the line drawn on top of it.
 */
export function TrendChart<T>({
  data,
  series,
  height = 288,
  xKey = 'day',
  formatRightAxis,
  formatLeftAxis,
  highlightedX,
  onPointClick,
}: TrendChartProps<T>) {
  const usesRightAxis = series.some((s) => s.axis === 'right');
  const ordered = [
    ...series.filter((s) => (s.kind ?? 'area') === 'bar'),
    ...series.filter((s) => (s.kind ?? 'area') === 'area'),
    ...series.filter((s) => s.kind === 'line'),
  ];
  const labelByKey = new Map(series.map((s) => [s.key, s.label]));
  const formatByKey = new Map(series.map((s) => [s.key, s.format]));

  return (
    <div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 4, right: usesRightAxis ? 8 : 16, left: 0 }}
            style={onPointClick ? { cursor: 'pointer' } : undefined}
            onClick={(state) => {
              if (!onPointClick) return;
              const label = state?.activeLabel;
              if (label == null) return;
              onPointClick(String(label));
            }}
          >
            <defs>
              {series
                .filter((s) => (s.kind ?? 'area') === 'area')
                .map((s) => (
                  <linearGradient
                    key={s.key}
                    id={`trend-fill-${s.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                  </linearGradient>
                ))}
            </defs>
            <CartesianGrid stroke={BRAND.linenDark} vertical={false} />
            <XAxis
              dataKey={xKey}
              stroke={BRAND.oldGrowth}
              fontSize={11}
              tickFormatter={(value: string) => formatShortDay(value)}
              minTickGap={16}
            />
            <YAxis
              yAxisId="left"
              stroke={BRAND.oldGrowth}
              fontSize={11}
              width={52}
              tickFormatter={formatLeftAxis}
            />
            {usesRightAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={BRAND.oldGrowth}
                fontSize={11}
                width={52}
                tickFormatter={formatRightAxis}
              />
            )}
            <Tooltip
              labelFormatter={(label) => formatTooltipDay(String(label))}
              formatter={(value, key) => {
                const name = String(key);
                const format = formatByKey.get(name);
                const numeric = typeof value === 'number' ? value : Number(value);
                return [
                  format ? format(numeric) : Math.round(numeric).toLocaleString(),
                  labelByKey.get(name) ?? name,
                ];
              }}
              contentStyle={{
                background: '#fff',
                border: `1px solid ${BRAND.linenDark}`,
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            {highlightedX && (
              <ReferenceLine
                x={highlightedX}
                stroke={BRAND.humus}
                strokeDasharray="3 3"
                strokeOpacity={0.45}
              />
            )}
            {ordered.map((s) => {
              const axisId = s.axis ?? 'left';
              const kind = s.kind ?? 'area';
              if (kind === 'bar') {
                return (
                  <Bar
                    key={s.key}
                    yAxisId={axisId}
                    dataKey={s.key}
                    fill={s.color}
                    radius={s.stackId ? [0, 0, 0, 0] : [3, 3, 0, 0]}
                    maxBarSize={28}
                    stackId={s.stackId}
                    cursor={onPointClick ? 'pointer' : undefined}
                  >
                    {highlightedX
                      ? data.map((point, index) => {
                          const xValue = String(
                            (point as Record<string, unknown>)[xKey] ?? '',
                          );
                          return (
                            <Cell
                              key={`${s.key}-${index}`}
                              fill={s.color}
                              fillOpacity={xValue === highlightedX ? 1 : 0.35}
                            />
                          );
                        })
                      : null}
                  </Bar>
                );
              }
              if (kind === 'line') {
                return (
                  <Line
                    key={s.key}
                    yAxisId={axisId}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={1.8}
                    strokeDasharray={s.dashed ? '4 3' : undefined}
                    dot={false}
                  />
                );
              }
              return (
                <Area
                  key={s.key}
                  yAxisId={axisId}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeWidth={2}
                  fill={`url(#trend-fill-${s.key})`}
                />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
        {series.map((s) => (
          <Fragment key={s.key}>
            <LegendDot color={s.color} label={s.label} dashed={s.dashed} />
          </Fragment>
        ))}
      </div>
    </div>
  );
}
